import { Color, Logo, LogoVariant } from '@gousto-internal/citrus-react';
import { AvailabilityIcon } from './AvailabilityIcon';
import { AVAILABILITY_LABELS, type ComparisonRow } from './comparisonTypes';
import {
  AvailabilityLabel,
  AvailabilityStack,
  BestValueBadge,
  DataCell,
  FromCaption,
  GoustoLogoWrapper,
  HeaderCell,
  HiddenCaption,
  PillBandCell,
  RowHeaderCell,
  Scroller,
  Table,
  ValueText,
} from './ComparisonTable.styles';

export type ComparisonTableProps = {
  readonly providers: readonly string[];
  readonly highlightedProvider: string;
  readonly rows: readonly ComparisonRow[];
  readonly ariaLabel: string;
  readonly caption: string;
  readonly gridArea?: string;
  readonly backgroundColor?: Color;
};

function highlighted(isHighlighted: boolean): 'true' | 'false' {
  return isHighlighted ? 'true' : 'false';
}

export function ComparisonTable({
  providers,
  highlightedProvider,
  rows,
  ariaLabel,
  caption,
  gridArea,
  backgroundColor,
}: ComparisonTableProps): JSX.Element {
  return (
    <Scroller role="region" aria-label={ariaLabel} tabIndex={0} gridArea={gridArea}>
      <Table backgroundColor={backgroundColor}>
        <HiddenCaption>{caption}</HiddenCaption>
        <colgroup>
          <col />
          {providers.map((provider) => (
            <col key={provider} />
          ))}
        </colgroup>
        <thead>
          <tr>
            <td aria-hidden />
            {providers.map((provider) =>
              provider === highlightedProvider ? (
                <PillBandCell key={provider} data-highlighted="true">
                  <BestValueBadge data-testid="best-value-badge">Best value</BestValueBadge>
                </PillBandCell>
              ) : (
                <td key={provider} aria-hidden />
              ),
            )}
          </tr>
          <tr>
            <td aria-hidden />
            {providers.map((provider) => {
              const isHighlighted = provider === highlightedProvider;
              return (
                <HeaderCell
                  key={provider}
                  scope="col"
                  data-highlighted={highlighted(isHighlighted)}
                >
                  {isHighlighted ? (
                    <GoustoLogoWrapper role="img" aria-label={provider}>
                      <Logo logoVariant={LogoVariant.Primary} aria-hidden />
                    </GoustoLogoWrapper>
                  ) : (
                    <div>{provider}</div>
                  )}
                </HeaderCell>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.label}>
              <RowHeaderCell scope="row">{row.label}</RowHeaderCell>
              {providers.map((provider) => {
                const isHighlighted = provider === highlightedProvider;
                const value = row.values[provider];

                if (!value) {
                  return <DataCell key={provider} data-highlighted={highlighted(isHighlighted)} />;
                }

                return (
                  <DataCell key={provider} data-highlighted={highlighted(isHighlighted)}>
                    {value.kind === 'price' && (
                      <>
                        <FromCaption>from</FromCaption>
                        <ValueText data-highlighted={highlighted(isHighlighted)}>
                          {value.amount}
                        </ValueText>
                      </>
                    )}
                    {value.kind === 'text' && (
                      <ValueText data-highlighted={highlighted(isHighlighted)}>
                        {value.text}
                      </ValueText>
                    )}
                    {value.kind === 'availability' && (
                      <AvailabilityStack>
                        <AvailabilityIcon state={value.state} />
                        <AvailabilityLabel>{AVAILABILITY_LABELS[value.state]}</AvailabilityLabel>
                      </AvailabilityStack>
                    )}
                  </DataCell>
                );
              })}
            </tr>
          ))}
        </tbody>
      </Table>
    </Scroller>
  );
}
